import type { AgentResponse, UIMessage, UUID as CoreUUID } from '@dopomogai/types';

/** A custom, identifiable error class for the agent-kit SDK. */
export class AgentKitError extends Error {
  /** The underlying error or API response that caused this error. */
  public readonly cause?: unknown;

  /** Optional HTTP-like status code if the cause has one (e.g., from API client). */
  public readonly status?: number;

  /** Optional error code from backend or transport layer. */
  public readonly code?: string | number;

  /** Optional details/errors blob passed through from the cause. */
  public readonly details?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'AgentKitError';
    this.cause = cause;

    // Best-effort: copy common fields from the cause if present
    const c: any = cause;
    if (c && typeof c === 'object') {
      if (typeof c.status === 'number') {
        (this as any).status = c.status;
      }
      if (typeof c.code !== 'undefined') {
        (this as any).code = c.code;
      }
      if (typeof c.details !== 'undefined') {
        (this as any).details = c.details;
      } else if (typeof c.errors !== 'undefined') {
        (this as any).details = c.errors;
      }
      // If message was empty and cause has a message, propagate it
      if (!message && typeof c.message === 'string') {
        this.message = c.message;
      }
    }
  }
}

/** Represents a single Agent resource. We alias AgentResponse for clarity. */
export type Agent = AgentResponse;

/**
 * Options for the sendMessage function in the useConversation hook.
 * This is part of the SDK's public API.
 */
export interface SendMessageOptions {
  /**
   * Provides additional context to the agent's system prompt for this turn only.
   * The SDK manages the creation and cleanup of a temporary prompt.
   * NOTE: This feature is temporarily disabled for stability.
   */
  injectionContext?: string;
  /** Injects a one-turn system prompt override for this request only */
  turnPromptOverride?: string;
}

// Re-export core types for convenience for consumers of the SDK.
export type { UIMessage };
export type UUID = CoreUUID;